import type { PartnerCustomModelWrite } from '../../api.js';
import type { ThinkingLevel } from '../../types.js';
import { modelFixedThinkingPreset } from '../../../../shared-types/src/model-thinking.js';

const PARTNER_THINKING_LEVELS: readonly ThinkingLevel[] = [
  'off',
  'auto',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh'
];

export function partnerThinkingLevelOptions(
  capabilities: PartnerCustomModelWrite['capabilities'],
  modelId?: string,
  stored?: readonly ThinkingLevel[]
): ThinkingLevel[] {
  if (!capabilities.reasoning) return ['off'];
  const preset = modelFixedThinkingPreset(modelId);
  const supportsAuto =
    capabilities.thinkingAuto?.strategy !== undefined &&
    capabilities.thinkingAuto.strategy !== 'unsupported';
  const options = preset
    ? [...preset, ...(supportsAuto ? ['auto' as const] : [])]
    : [...PARTNER_THINKING_LEVELS, ...(stored?.includes('max') ? ['max' as const] : [])];
  return options.filter(
    (level) => level !== 'auto' || supportsAuto
  );
}

export function partnerThinkingLevelSelection(
  options: readonly ThinkingLevel[],
  stored?: readonly ThinkingLevel[]
): ThinkingLevel[] {
  if (stored === undefined) return [];
  const selected = new Set(stored);
  return options.filter((level) => selected.has(level));
}

export function partnerThinkingLevelsSupported(
  options: readonly ThinkingLevel[]
): boolean {
  return options.some((level) => level !== 'off');
}

export function partnerThinkingLevelsPayload(
  options: readonly ThinkingLevel[],
  selection: readonly ThinkingLevel[],
  stored?: readonly ThinkingLevel[]
): ThinkingLevel[] | undefined {
  const selected = new Set(selection);
  const normalized = options.filter((level) => selected.has(level));
  if (normalized.length === 0) return undefined;

  if (stored !== undefined) {
    const normalizedStored = partnerThinkingLevelSelection(options, stored);
    if (
      normalizedStored.length === stored.length &&
      normalizedStored.length === normalized.length &&
      normalizedStored.every((level) => selected.has(level))
    ) {
      return [...stored];
    }
    return normalized;
  }

  return normalized;
}
